import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";
import { PartyPoster } from "@/data/partiesStore";
import { uploadFile } from "@/utils/uploadHelpers";

interface AdminPosterFormDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialAcronym?: string | null;
  initialPoster?: PartyPoster | null;
  customParties?: { acronym: string }[];
  onSave: (acronym: string, poster: PartyPoster, customData?: { acronym: string; fullNameFr: string; fullNameAr: string; color: string }) => void;
}

export default function AdminPosterFormDialog({ children, open, onOpenChange, initialAcronym, initialPoster, customParties = [], onSave }: AdminPosterFormDialogProps) {
  const [acronym, setAcronym] = useState<string>(initialAcronym || allPartiesIncludingNonParliamentary[0].acronym);
  const [formData, setFormData] = useState<Partial<PartyPoster>>({
    isActive: true,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const [customData, setCustomData] = useState({
    acronym: "", fullNameFr: "", fullNameAr: "", color: "#3b82f6"
  });

  useEffect(() => {
    if (open) {
      setAcronym(initialAcronym || allPartiesIncludingNonParliamentary[0].acronym);
      if (initialPoster) {
        setFormData(initialPoster);
      } else {
        setFormData({
          url: "",
          titleFr: "",
          titleAr: "",
          descriptionFr: "",
          isActive: true
        });
      }
    }
  }, [open, initialAcronym, initialPoster]);

  const handleChange = (field: keyof PartyPoster, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const url = await uploadFile(file);
      setFormData(prev => ({ ...prev, url }));
    } catch (error) {
      console.error("Erreur d'upload:", error);
      alert("Erreur lors de l'upload du fichier.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCustom) {
      if (customData.acronym && formData.url) {
        onSave(customData.acronym, formData as PartyPoster, customData);
        if (onOpenChange) onOpenChange(false);
      }
    } else {
      if (acronym && formData.url) {
        onSave(acronym, formData as PartyPoster);
        if (onOpenChange) onOpenChange(false);
      }
    }
  };

  const isEditing = !!initialPoster;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? `Modifier l'affiche - ${acronym}` : "Ajouter une affiche de campagne"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Sélectionnez un parti politique</Label>
                <select 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-bold" 
                  value={isCustom ? "_custom_" : acronym} 
                  onChange={e => {
                    const val = e.target.value;
                    if (val === "_custom_") {
                      setIsCustom(true);
                      setAcronym("");
                    } else {
                      setIsCustom(false);
                      setAcronym(val);
                    }
                  }}
                  required
                >
                  <option value="" disabled>-- Choisir un parti --</option>
                  <option value="_custom_" className="font-bold text-primary">➕ Ajouter un nouveau parti...</option>
                  {customParties.map(p => (
                    <option key={p.acronym} value={p.acronym}>{p.acronym} (Personnalisé)</option>
                  ))}
                  {allPartiesIncludingNonParliamentary.map(p => (
                    <option key={p.acronym} value={p.acronym}>{p.acronym}</option>
                  ))}
                </select>
              </div>
              
              {isCustom && (
                <div className="bg-primary/5 border border-primary/20 p-4 rounded-xl space-y-4">
                  <h4 className="font-bold text-sm text-primary">Informations du Nouveau Parti</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Acronyme / Symbole</Label>
                      <Input placeholder="ex: INDEP" value={customData.acronym} onChange={e => setCustomData({...customData, acronym: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Couleur Principale</Label>
                      <div className="flex gap-2">
                        <Input type="color" className="w-12 p-1" value={customData.color} onChange={e => setCustomData({...customData, color: e.target.value})} />
                        <Input value={customData.color} onChange={e => setCustomData({...customData, color: e.target.value})} />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom complet (FR)</Label>
                      <Input placeholder="ex: Indépendants Unis" value={customData.fullNameFr} onChange={e => setCustomData({...customData, fullNameFr: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-right block">Nom complet (AR)</Label>
                      <Input dir="rtl" placeholder="ex: مستقلون" value={customData.fullNameAr} onChange={e => setCustomData({...customData, fullNameAr: e.target.value})} required />
                    </div>
                  </div>
                </div>
              )}
            </div>

          <div className="space-y-2 bg-muted/30 p-4 rounded-xl border border-border">
            <Label>Image de l'affiche (Upload)</Label>
            {formData.url && (
              <div className="mb-4 rounded-lg overflow-hidden border border-border h-48 bg-card flex items-center justify-center">
                <img src={formData.url} alt="Aperçu" className="max-h-full object-contain" />
              </div>
            )}
            <Input 
              type="file" 
              accept="image/*" 
              onChange={handleFileUpload} 
              required={!formData.url}
              className="cursor-pointer file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20" 
            />
            {isUploading && <p className="text-xs text-primary font-bold mt-1">Upload en cours...</p>}
            <p className="text-xs text-muted-foreground mt-1">L'image sera sauvegardée sur le serveur.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Slogan / Titre (FR)</Label>
              <Input value={formData.titleFr || ""} onChange={e => handleChange("titleFr", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label className="text-right block">Slogan / Titre (AR)</Label>
              <Input dir="rtl" value={formData.titleAr || ""} onChange={e => handleChange("titleAr", e.target.value)} required />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Description Courte</Label>
              <Input value={formData.descriptionFr || ""} onChange={e => handleChange("descriptionFr", e.target.value)} />
            </div>
          </div>

          <div className="flex items-center justify-between bg-primary/5 p-4 rounded-xl border border-primary/20">
            <div className="space-y-0.5">
              <Label className="text-base font-bold text-primary">Publier l'affiche</Label>
              <p className="text-xs text-muted-foreground">Si désactivé, l'affiche sera masquée au public.</p>
            </div>
            <Switch 
              checked={formData.isActive ?? true} 
              onCheckedChange={v => handleChange("isActive", v)} 
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange && onOpenChange(false)} disabled={isUploading}>Annuler</Button>
            <Button type="submit" disabled={isUploading}>Sauvegarder l'affiche</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
